import { AuthProvider } from 'react-admin';
import { getJWT } from './tokenUtils';
import { customRequest } from '../dataProvider';

export { getJWT };

function parseJwt (token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        window.atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
    );
    return JSON.parse(jsonPayload);
}

function log (...args: any[]): void {
    const storedOutput = JSON.parse(localStorage.getItem('consoleOutput') || '[]');
    storedOutput.push({
        args,
        url: document.location.href,
        timestamp: new Date().getTime()
    });
    localStorage.setItem('consoleOutput', JSON.stringify(storedOutput));
    console.log(...args);
}

interface TokenData {
    jwt: string;
}

const getProfileFromToken = (tokenJson: string) => {
    const token: TokenData = JSON.parse(tokenJson);
    const jwt = parseJwt(token.jwt);
    return { id: 'my-profile', ...jwt };
};

async function exchangeOTPforJWT (otp: string): Promise<TokenData> {
    const response = await fetch('https://identity.songtek.net/jwt/exchange', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ otp })
    });
    if (!response.ok) {
        throw new Error(await response.text());
    }
    return await response.json();
}

function fireAuthEvent (eventName: string): void {
    const event = new CustomEvent('devsoftAuthEvent', {
        detail: { authState: eventName }
    });
    document.dispatchEvent(event);
}

window.addEventListener('storage', (ev: StorageEvent) => {
    if (ev.key === 'token') {
        log('message_receive: token changed. Reason: From another tab or window');
        fireAuthEvent('authenticated');
    }
});

/**
 * The build identifier injected at compile time by esbuild.
 *
 * In CI the Azure DevOps pipeline sets this to `BUILD_BUILDID` (a unique
 * numeric build number). During local development the value is `"Local"`.
 *
 * @see build-pipeline.yaml  – production injection
 * @see server.js            – local dev injection
 */
const BUILD_ID: string = process.env.REACT_APP_BUILD_ID ?? 'Local';

/**
 * Returns `true` when the application is running in local development mode
 * (i.e. the build ID was not set by the CI pipeline).
 */
const isLocalDev = (): boolean => !BUILD_ID || BUILD_ID === 'Local';

/**
 * sessionStorage key used to cache the current user's granted permission
 * scopes. The build ID is embedded so that every deployment gets a fresh
 * cache — deployed builds keep their permissions indefinitely, while local
 * development always bypasses the cache entirely.
 */
const PERMISSIONS_CACHE_KEY = `hr_permissions_cache_v_${BUILD_ID}`;

/**
 * Shape of the object stored in sessionStorage under {@link PERMISSIONS_CACHE_KEY}.
 */
interface PermissionsCache {
    permissions: string[];
    buildId: string;
}

export const authProvider: AuthProvider = {
    login: async ({ otp }: { otp: string }) => {
        if (!otp) {
            throw new Error('Unset OTP');
        }
        log('login: exchangeOTPforJWT and store token into local storage');
        const token = await exchangeOTPforJWT(otp);
        localStorage.setItem('token', JSON.stringify(token));
        fireAuthEvent('authenticated');
        return Promise.resolve({ redirectTo: '/' });
    },
    logout: () => {
        log('logout: remove token from local storage');
        localStorage.removeItem('token');
        sessionStorage.clear();
        return Promise.resolve();
    },
    checkAuth: () => {
        const token = localStorage.getItem('token');

        if (!token) {
            log('checkAuth: false. Reason: token not found in local storage');
            return Promise.reject();
        }

        const jwt = getProfileFromToken(token);
        const now = new Date();
        const hasExpired = now.getTime() > (jwt.exp * 1000);
        log(`checkAuth: ${!hasExpired}. Reason: check hasExpired=${hasExpired}`);
        
        if (hasExpired) {
            fireAuthEvent('unauthenticated');
            return Promise.reject();
        }
        
        fireAuthEvent('authenticated');
        return Promise.resolve();
    },
    checkError: (error: any) => {
        const status = error.status;
        if (status === 401) {
            log('checkError: Asking for relogin. Reason: ' + status);
            fireAuthEvent('unauthenticated');
            localStorage.removeItem('token');
            return Promise.reject();
        }
        if (status === 403) {
            log('checkError: redirect to /error/403. Reason: ' + status);
            return Promise.reject({ redirectTo: '/error/403', logoutUser: false });
        }
        log('checkError: other error code (404, 500, etc): no need to log out. Reason: ' + status);
        return Promise.resolve();
    },
    getIdentity: async () => {
        const tokenJson = localStorage.getItem('token');
        if (!tokenJson) {
            throw new Error('Not logged in');
        }
        const user = await customRequest('whoami').then(
            ({ json }) => json as { id: string; email: string; fullName: string }
        );
        return { id: user.id, fullName: user.fullName, email: user.email };
    },
    /**
     * Fetches the authenticated user's granted permission scopes.
     *
     * **Caching strategy (version-based):**
     * - In **deployed builds** (`REACT_APP_BUILD_ID` is a pipeline build number)
     *   permissions are cached in `sessionStorage` and keyed by build ID.  Because
     *   each deployment produces a unique key the cache is implicitly invalidated
     *   whenever a new version is deployed — no time-based TTL is needed.
     * - In **local development** (`REACT_APP_BUILD_ID === "Local"`) the cache is
     *   bypassed entirely so that permission changes are always reflected immediately.
     *
     * @returns Array of permission scope strings the user has been granted.
     */
    getPermissions: async () => {
        const tokenJson = localStorage.getItem('token');
        if (!tokenJson) {
            return [];
        }

        // --- Cache lookup (skip entirely for local dev) ---
        if (!isLocalDev()) {
            const cached = sessionStorage.getItem(PERMISSIONS_CACHE_KEY);
            if (cached !== null) {
                try {
                    const entry = JSON.parse(cached) as PermissionsCache;
                    if (entry.buildId === BUILD_ID) {
                        return entry.permissions;
                    }
                    // Build ID mismatch — stale entry from a previous deployment.
                    sessionStorage.removeItem(PERMISSIONS_CACHE_KEY);
                } catch {
                    // Cached value is corrupted — discard and re-fetch.
                    sessionStorage.removeItem(PERMISSIONS_CACHE_KEY);
                }
            }
        }

        // Check all relevant permission scopes in a single batch request.
        const scopesToCheck = [
            'timeoff.read',
            'timeoff.approve',
            'timeoff.approve_all',
            'timeoff.create_on_behalf',
            'timeoff.export',
            'timeoff.import',
            'timeoff.view_all_requests',
            'users.read',
            'users.list',
            'users.export',
            'users.manage',
            'work-schedules.read',
            'overtime.read',
            'overtime.create',
            'overtime.cancel',
            'overtime.create_on_behalf',
            'overtime.view_all',
            'night-shift-declarations.read',
            'night-shift-declarations.create',
            'night-shift-declarations.admin',
            'employees.read',
            'employees.manage',
            'employees.export',
            'terminations.read',
            'terminations.manage',
            'timesheets.manage',
            'holidays.manage'
        ];
        try {
            const { json } = await customRequest('check-permissions', {
                method: 'POST',
                body: JSON.stringify({ scopes: scopesToCheck }),
            });
            const permitted = (json as { permitted: string[] }).permitted;

            // Persist to sessionStorage only for deployed (non-local) builds.
            if (!isLocalDev()) {
                const entry: PermissionsCache = { permissions: permitted, buildId: BUILD_ID };
                sessionStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(entry));
            }
            return permitted;
        } catch (e) {
            console.error('Error checking permissions:', e);
            return [];
        }
    },
};
